import { useLiveQuery } from 'dexie-react-hooks';
import { useWorkspace } from '@/app/providers/workspace-provider';
import type { BoardColumn, BoardPlacement, MatrixPlacement, Task, Uuid } from '@/data/db';

/**
 * 讀取資料一律走 liveQuery，不另外複製一份長期全域 store（plan.md 3.3）。
 * 這些 hook 只負責讀；所有寫入都走 repository。
 */

export function useActiveTasks(): Task[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(async () => {
    const tasks = await db.tasks.orderBy('number').reverse().toArray();
    return tasks.filter((task) => task.deletedAt === null);
  }, [db]);
}

export function useTask(id: Uuid | null): Task | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(async () => (id ? db.tasks.get(id) : undefined), [db, id]);
}

export function useNotesFor(taskId: Uuid | null): Task[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(async () => {
    if (!taskId) return [];
    const notes = await db.tasks.where('linkedTaskId').equals(taskId).toArray();
    return notes.filter((note) => note.deletedAt === null);
  }, [db, taskId]);
}

export function useBoardColumns(): BoardColumn[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(() => db.boardColumns.orderBy('order').toArray(), [db]);
}

export function useBoardPlacements(): BoardPlacement[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(() => db.boardPlacements.toArray(), [db]);
}

export function useMatrixPlacements(): MatrixPlacement[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(() => db.matrixPlacements.toArray(), [db]);
}

export function useDeletedTasks(): Task[] | undefined {
  const { db } = useWorkspace();
  return useLiveQuery(async () => {
    const tasks = await db.tasks.orderBy('number').toArray();
    return tasks.filter((task) => task.deletedAt !== null);
  }, [db]);
}
